import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { MAX_INPUT_LENGTH_2048 } from '../../../../../../../../constants';
import styles from './TreatmentBlock.module.scss';
import { Button, RadioButton, Text, Textarea } from 'components';
import { appActions } from 'store/app/slice';
import { patientsActions } from 'store/patients/slice';
import { useDispatch } from 'store/utils';
import { onlyLatinLettersSymbolsNumbers } from 'utils';

type Props = {
  id: number;
  diagnosis: string;
  treatmentPlan: string;
  onClose(): void;
  allowNoneAligner: boolean;
};

const schema = yup.object({
  treatment: yup
    .string()
    .matches(onlyLatinLettersSymbolsNumbers, {
      message: 'Only Latin Alphabet is allowed',
    })
    .required('The field is empty'),
  treatmentPlanType: yup.string(),
});

export const TreatmentBlock = ({ diagnosis, treatmentPlan, id, onClose, allowNoneAligner }: Props) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch(patientsActions.setShouldPatientPageUpdate(false));
    dispatch(patientsActions.setIsPatientPageEdited(true));
    return () => {
      dispatch(patientsActions.setShouldPatientPageUpdate(true));
      dispatch(patientsActions.setIsPatientPageEdited(false));
    };
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      treatment: treatmentPlan ?? '',
    },
  });

  const onSubmitTreatment = handleSubmit(async (formData) => {
    if (isLoading) return;
    try {
      setIsLoading(true);
      const wrappedResult = dispatch(
        patientsActions.updatePatientDiagnosisAndTreatment({
          patientCaseData: {
            diagnosis: diagnosis ?? null,
            treatmentPlan: formData.treatment,
          },
          patientCaseId: id,
        }),
      );
      await wrappedResult.unwrap();
      onClose();
      dispatch(appActions.setModalErrorText({ name: 'treatmentAndDiagnosisError', errorText: '' }));
    } catch (error: any) {
      dispatch(appActions.setModalErrorText({ name: 'treatmentAndDiagnosisError', errorText: error?.title }));
      dispatch(appActions.setIsModalOpened({ isOpened: true, name: 'treatmentAndDiagnosisError' }));
      onClose();
    } finally {
      setIsLoading(false);
    }
  });

  const watchedTreatment = watch('treatment');
  return (
    <form className={styles.inputItem}>
      <Textarea
        labelText=""
        placeholder="Please fill in the treatment plan"
        inputName="treatment"
        register={register}
        maxLength={MAX_INPUT_LENGTH_2048}
        autoComplete="off"
        characterCounter
        errorMsg={errors.treatment?.message as string}
        currentValue={watchedTreatment}
        setValue={setValue}
        isForLinks={false}
      />
      <div className={styles.buttonRow}>
        <Button onClick={onClose}>Cancel</Button>
        <Button className={styles.submitButton} onClick={onSubmitTreatment}>
          Save
        </Button>
      </div>
    </form>
  );
};
